import React, { useEffect, useState } from 'react';

// What each pattern ID does on each product. Transcribed from the firmware's
// pattern library (nrf-connect/application/src/motor_control.c) — keep in step.
// A step lasts one second; each step maps motor number -> strength (% of the
// phase intensity). Steady patterns have a single step.

type Step = Record<number, number>;

interface Pattern {
  id: number;
  name: string;
  description: string;
  steps: Step[]; // empty = no motor output
  unavailable?: boolean;
}

interface ProductPatterns {
  code: string;
  label: string;
  motors: number[];
  patterns: Pattern[];
}

const on = (...motors: number[]): Step =>
  Object.fromEntries(motors.map(m => [m, 100]));

const RAMP = [40, 60, 80, 100, 100, 80, 60, 40];

const SILENT: Pattern = {
  id: 8, name: 'Silent', steps: [],
  description: 'No vibration. The phase still runs for its full time (for testing).',
};

const unavailable = (id: number): Pattern => ({
  id, name: 'Not available', steps: [], unavailable: true,
  description: 'Not supported on this product — don\'t use.',
});

const PRODUCTS: ProductPatterns[] = [
  {
    code: 'VM6MOAABLE', label: 'Back Recover 6 Motor', motors: [1, 2, 3, 4, 5, 6],
    patterns: [
      { id: 1, name: 'Pair sweep', description: 'Pairs take turns: 1+6, 2+5, 3+4, 2+5, repeat.', steps: [on(1, 6), on(2, 5), on(3, 4), on(2, 5)] },
      { id: 2, name: 'Rolling wave', description: 'A wave rolls across the motors: 5, 4+6, 1+3, 2, 1+3, 4+6, repeat.', steps: [on(5), on(4, 6), on(1, 3), on(2), on(1, 3), on(4, 6)] },
      { id: 3, name: 'Outer four', description: 'Motors 1, 2, 5, 6 on steadily.', steps: [on(1, 2, 5, 6)] },
      { id: 4, name: 'Half toggle', description: 'Motors 1–3, then 4–6, switching every second.', steps: [on(1, 2, 3), on(4, 5, 6)] },
      { id: 5, name: 'All on', description: 'All six motors on steadily.', steps: [on(1, 2, 3, 4, 5, 6)] },
      { id: 6, name: 'Alternating', description: 'Motors 1, 3, 4, 6 on steadily.', steps: [on(1, 3, 4, 6)] },
      {
        id: 7, name: 'Ramping wave', description: 'Motors 2 and 5 build up and ease off, with 3+4 and 1+6 pulsing at the peaks.',
        steps: [
          { 1: 100, 2: 40, 5: 40, 6: 100 }, { 2: 60, 5: 60 }, { 2: 80, 3: 100, 4: 100, 5: 80 }, { 2: 100, 5: 100 },
          { 1: 100, 2: 100, 5: 100, 6: 100 }, { 2: 80, 5: 80 }, { 2: 60, 3: 100, 4: 100, 5: 60 }, { 2: 40, 5: 40 },
        ],
      },
      SILENT,
    ],
  },
  {
    code: 'VM4MOAABLE', label: 'Back Recover 4 Motor', motors: [1, 3, 4, 6],
    patterns: [
      { id: 1, name: 'Pair sweep', description: 'Inner pair 3+4, then outer pair 1+6, switching every second.', steps: [on(3, 4), on(1, 6)] },
      { id: 2, name: 'Ping-pong', description: 'One motor at a time: 1, 3, 4, 6, 4, 3, repeat.', steps: [on(1), on(3), on(4), on(6), on(4), on(3)] },
      { id: 3, name: 'Outer pair', description: 'Motors 1 and 6 on steadily.', steps: [on(1, 6)] },
      { id: 4, name: 'Half toggle', description: 'Motors 1+3, then 4+6, switching every second.', steps: [on(1, 3), on(4, 6)] },
      { id: 5, name: 'All on', description: 'All four motors on steadily.', steps: [on(1, 3, 4, 6)] },
      unavailable(6),
      unavailable(7),
      SILENT,
    ],
  },
  {
    code: 'VM2MOBABLE', label: '2 Motor', motors: [1, 2],
    patterns: [
      { id: 1, name: 'Both on', description: 'Both motors on steadily.', steps: [on(1, 2)] },
      { id: 2, name: 'Alternate', description: 'Motor 1, then motor 2, switching every second.', steps: [on(1), on(2)] },
      { id: 3, name: 'Motor 1 only', description: 'Motor 1 on steadily.', steps: [on(1)] },
      { id: 4, name: 'Motor 2 only', description: 'Motor 2 on steadily.', steps: [on(2)] },
      { id: 5, name: 'Both on', description: 'Same as pattern 1.', steps: [on(1, 2)] },
      { id: 6, name: 'Wave', description: 'Both motors build up and ease off together.', steps: RAMP.map(r => ({ 1: r, 2: r })) },
      { id: 7, name: 'Crossfade', description: 'One motor builds up as the other eases off — a soft side-to-side.', steps: RAMP.map((r, s) => ({ 1: r, 2: RAMP[(s + 4) % 8] })) },
      SILENT,
    ],
  },
  {
    code: 'VM1MOAABLE', label: '1 Motor', motors: [1],
    patterns: [
      { id: 1, name: 'On', description: 'Motor on steadily.', steps: [on(1)] },
      { id: 2, name: 'Silent', description: 'No vibration.', steps: [] },
      { id: 3, name: 'Silent', description: 'No vibration.', steps: [] },
      { id: 4, name: 'Silent', description: 'No vibration.', steps: [] },
      { id: 5, name: 'On', description: 'Same as pattern 1.', steps: [on(1)] },
      unavailable(6),
      unavailable(7),
      SILENT,
    ],
  },
];

// ─── Motor dots — animate through the pattern's steps, one per second ─────────

const MotorDots: React.FC<{ motors: number[]; pattern: Pattern; tick: number }> = ({ motors, pattern, tick }) => {
  const step: Step = pattern.steps.length ? pattern.steps[tick % pattern.steps.length] : {};
  return (
    <div className="flex gap-2">
      {motors.map(m => {
        const level = step[m] ?? 0;
        return (
          <div key={m} className="flex flex-col items-center gap-1">
            <div className="relative w-9 h-9 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
              <div
                className="absolute inset-0 rounded-full bg-MyovoltPurple transition-opacity duration-300"
                style={{ opacity: level / 100 }}
              />
            </div>
            <span className="text-[11px] text-gray-500">M{m}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Modal ───────────────────────────────────────────────────────────────────

const ViewPatterns: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [selected, setSelected] = useState(PRODUCTS[0]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        <div className="px-7 pt-7 pb-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">Vibration Patterns</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Which motors run for each pattern number. Darker = stronger; strength scales with the phase intensity.
            </p>
          </div>
          <button className="btn btn-sm btn-circle btn-ghost flex-shrink-0" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="px-7 pt-4 flex flex-wrap gap-2">
          {PRODUCTS.map(p => (
            <button
              key={p.code}
              onClick={() => setSelected(p)}
              className={`btn btn-sm ${p.code === selected.code ? 'btn-primary' : 'btn-ghost'}`}
            >{p.label}</button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-7 py-4">
          <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 overflow-hidden">
            {selected.patterns.map(p => (
              <div
                key={p.id}
                className={`flex flex-wrap items-center justify-between gap-4 px-4 py-3 bg-white ${p.unavailable ? 'opacity-40' : ''}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm flex items-center justify-center">
                    {p.id}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {p.name}
                      {p.steps.length > 1 && (
                        <span className="ml-2 text-xs font-normal text-gray-400">{p.steps.length} s cycle</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">{p.description}</p>
                  </div>
                </div>
                {!p.unavailable && <MotorDots motors={selected.motors} pattern={p} tick={tick} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPatterns;
